import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import Header from "@/components/Header";
import WeekSelector from "@/components/WeekSelector";
import CategoryFilter from "@/components/CategoryFilter";
import PromoCarousel from "@/components/PromoCarousel";
import MenuSection from "@/components/MenuSection";
import BasketModal from "@/components/BasketModal";
import CheckoutModal from "@/components/CheckoutModal";
import QuantityModal from "@/components/QuantityModal";
import { MAX_QTY } from "@/components/QuantityInput";
import { preloadAllImages } from "@/components/MenuCard";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Global cache - persists across component remounts
const globalCache = {
  allMenuItems: null,
  categories: null,
  promotions: null,
  rotatingCategoryKeys: new Set(),
  imagesPreloaded: false,
  timestamp: null,
};

const CACHE_DURATION = 10 * 60 * 1000; // 10 minutes

export default function MenuPage() {
  const [allMenuItems, setAllMenuItems] = useState(globalCache.allMenuItems || []);
  const [categories, setCategories] = useState(globalCache.categories || []);
  const [promotions, setPromotions] = useState(globalCache.promotions || []);
  const [loading, setLoading] = useState(!globalCache.allMenuItems);
  const [selectedWeek, setSelectedWeek] = useState(null);
  const [activeCategory, setActiveCategory] = useState(null);
  const [basket, setBasket] = useState([]);
  const isProgrammaticScrollRef = useRef(false);

  // Modal states
  const [basketModalOpen, setBasketModalOpen] = useState(false);
  const [checkoutModalOpen, setCheckoutModalOpen] = useState(false);
  const [quantityModalOpen, setQuantityModalOpen] = useState(false);
  const [quantityItem, setQuantityItem] = useState(null);

  const loadAllData = useCallback(async () => {
    const now = Date.now();

    if (globalCache.timestamp && now - globalCache.timestamp < CACHE_DURATION && globalCache.allMenuItems) {
      setAllMenuItems(globalCache.allMenuItems);
      setCategories(globalCache.categories);
      setPromotions(globalCache.promotions || []);
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      const [menuRes, catRes, promoRes] = await Promise.all([
        axios.get(`${API}/menu?include_inactive=false`),
        axios.get(`${API}/categories`),
        axios.get(`${API}/promotions/active`),
      ]);

      const sortedCategories = catRes.data.sort((a, b) => (a.order || 0) - (b.order || 0));
      const rotatingKeys = new Set(sortedCategories.filter((c) => c.is_rotating).map((c) => c.key));

      globalCache.allMenuItems = menuRes.data;
      globalCache.categories = sortedCategories;
      globalCache.promotions = promoRes.data;
      globalCache.rotatingCategoryKeys = rotatingKeys;
      globalCache.timestamp = now;

      setAllMenuItems(menuRes.data);
      setCategories(sortedCategories);
      setPromotions(promoRes.data);

      if (!globalCache.imagesPreloaded) {
        const allItemIds = menuRes.data.map((item) => item.id);
        preloadAllImages(allItemIds).then(() => {
          globalCache.imagesPreloaded = true;
        });
      }
    } catch {
      toast.error("Failed to load menu");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadAllData();
  }, [loadAllData]);

  // Filter items by selected week (client-side)
  const filteredMenuItems = useMemo(() => {
    if (!allMenuItems.length || !selectedWeek) return [];
    const weekNumber = selectedWeek.week_number;

    return allMenuItems.filter((item) => {
      const isRotating = globalCache.rotatingCategoryKeys.has(item.category);
      if (isRotating) return item.week_number === weekNumber;
      return true;
    });
  }, [allMenuItems, selectedWeek]);

  const getFilteredItems = useCallback(
    (categoryKey) => {
      return filteredMenuItems
        .filter((item) => item.category === categoryKey)
        .sort((a, b) => (a.display_order || 0) - (b.display_order || 0));
    },
    [filteredMenuItems]
  );

  // Scroll-spy: highlight the category whose section is currently in view
  useEffect(() => {
    if (!categories.length || loading) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (isProgrammaticScrollRef.current) return; // ignore during smooth-scroll
        // Pick the entry that is most visible at the top
        const visibleEntries = entries.filter((e) => e.isIntersecting);
        if (visibleEntries.length === 0) return;
        // Choose the one with the smallest top position (closest to top)
        const topMost = visibleEntries.reduce((best, current) =>
          current.boundingClientRect.top < best.boundingClientRect.top ? current : best
        );
        const key = topMost.target.getAttribute("data-category-key");
        if (key) setActiveCategory(key);
      },
      {
        // Active zone: just below the sticky header+filter bar (top 140px excluded)
        // and only the upper 40% of the viewport counts as "current"
        rootMargin: "-140px 0px -60% 0px",
        threshold: 0,
      }
    );

    const sections = document.querySelectorAll("section[data-category-key]");
    sections.forEach((s) => observer.observe(s));

    return () => observer.disconnect();
  }, [categories, loading, filteredMenuItems]);

  // Set initial active category to the first one once categories load
  useEffect(() => {
    if (categories.length && !activeCategory) {
      setActiveCategory(categories[0].key);
    }
  }, [categories, activeCategory]);

  const handleCategoryClick = useCallback((categoryKey) => {
    const section = document.getElementById(`category-${categoryKey}`);
    if (!section) return;
    // Optimistically update active state for instant feedback
    setActiveCategory(categoryKey);
    // Suppress scroll-spy briefly so it doesn't fight with smooth scroll
    isProgrammaticScrollRef.current = true;
    section.scrollIntoView({ behavior: "smooth", block: "start" });
    setTimeout(() => {
      isProgrammaticScrollRef.current = false;
    }, 700);
  }, []);

  const handleWeekChange = useCallback((week) => {
    setSelectedWeek(week);
  }, []);

  const handleAddToBasket = useCallback(
    (item, quantity, variant = null) => {
      const category = categories.find((c) => c.key === item.category);
      const weekNumber = category?.is_rotating ? selectedWeek?.week_number : null;

      setBasket((prev) => {
        const existingIndex = prev.findIndex((b) => b.item.id === item.id && b.variant?.name === variant?.name);
        if (existingIndex >= 0) {
          return prev.map((b, i) =>
            i === existingIndex ? { ...b, quantity: Math.min(MAX_QTY, b.quantity + quantity) } : b
          );
        }
        return [...prev, { item, quantity, variant, weekNumber }];
      });

      toast.success(`${item.name} added to basket`);
      setQuantityModalOpen(false);
    },
    [categories, selectedWeek]
  );

  const updateBasketQuantity = useCallback((index, qty) => {
    setBasket((prev) =>
      qty <= 0
        ? prev.filter((_, i) => i !== index)
        : prev.map((b, i) => (i === index ? { ...b, quantity: Math.min(MAX_QTY, qty) } : b))
    );
  }, []);

  const removeFromBasket = useCallback((index) => {
    setBasket((prev) => prev.filter((_, i) => i !== index));
  }, []);

  const clearBasket = useCallback(() => {
    setBasket([]);
    setBasketModalOpen(false);
  }, []);

  const basketTotal = useMemo(
    () =>
      basket.reduce((sum, b) => {
        const price = b.variant ? b.variant.price : b.item.price;
        return sum + price * b.quantity;
      }, 0),
    [basket]
  );

  const basketCount = useMemo(() => basket.reduce((sum, b) => sum + b.quantity, 0), [basket]);

  const openQuantityModal = useCallback((item) => {
    setQuantityItem(item);
    setQuantityModalOpen(true);
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-[#1aa59a] border-t-transparent rounded-full animate-spin mx-auto mb-3"></div>
          <p className="text-gray-600 text-sm">Loading menu...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 pb-24">
      <Header
        basketCount={basketCount}
        basketTotal={basketTotal}
        onBasketClick={() => setBasketModalOpen(true)}
      />

      <WeekSelector selectedWeek={selectedWeek} onWeekChange={handleWeekChange} />

      <CategoryFilter
        categories={categories}
        activeCategory={activeCategory}
        onSelectCategory={handleCategoryClick}
      />

      <main className="max-w-7xl mx-auto px-4 py-4">
        {promotions.length > 0 && <PromoCarousel promotions={promotions} />}

        {categories.map((category) => (
          <MenuSection
            key={category.key}
            category={category}
            items={getFilteredItems(category.key)}
            onAddToBasket={openQuantityModal}
          />
        ))}
      </main>

      <BasketModal
        open={basketModalOpen}
        onClose={() => setBasketModalOpen(false)}
        basket={basket}
        onUpdateQuantity={updateBasketQuantity}
        onRemove={removeFromBasket}
        onCheckout={() => {
          setBasketModalOpen(false);
          setCheckoutModalOpen(true);
        }}
        total={basketTotal}
      />

      <CheckoutModal
        open={checkoutModalOpen}
        onClose={() => setCheckoutModalOpen(false)}
        basket={basket}
        total={basketTotal}
        selectedWeek={selectedWeek}
        onSuccess={clearBasket}
      />

      <QuantityModal
        open={quantityModalOpen}
        onClose={() => setQuantityModalOpen(false)}
        item={quantityItem}
        onConfirm={handleAddToBasket}
      />
    </div>
  );
}
